import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

import { SiteProvider } from './context/SiteContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import Tracker from './components/Tracker';

import PublicLayout from './components/PublicLayout';
import Home from './pages/Home';
import Shop from './pages/Shop';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetail from './pages/CollectionDetail';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Confirmation from './pages/Confirmation';
import CmsPage from './pages/CmsPage';
import DynamicCmsPage from './pages/DynamicCmsPage';
import ContactPage from './pages/ContactPage';
import Account from './pages/Account';

import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminCollections from './pages/admin/Collections';
import AdminRequests from './pages/admin/Requests';
import AdminPages from './pages/admin/Pages';
import AdminNavigation from './pages/admin/Navigation';
import AdminSettings from './pages/admin/Settings';
import AdminNewsletter from './pages/admin/Newsletter';
import AdminMessages from './pages/admin/Messages';
import AdminAudience from './pages/admin/Audience';
import AdminCarts from './pages/admin/Carts';

function RequireAdmin({ children }) {
    const { isAuthed, loading } = useAdminAuth();
    if (loading) return null;
    if (!isAuthed) return <Navigate to="/admin/login" replace />;
    return children;
}

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <SiteProvider>
                    <CurrencyProvider>
                    <CartProvider>
                        <CustomerAuthProvider>
                        <AdminAuthProvider>
                            <Tracker />
                            <Toaster
                                position="top-center"
                                toastOptions={{
                                    style: { background: '#fff', color: '#1A1416', border: '1px solid rgba(90,36,50,0.14)' },
                                }}
                            />
                            <Routes>
                                {/* Public */}
                                <Route element={<PublicLayout />}>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/shop" element={<Shop />} />
                                    <Route path="/collections" element={<CollectionsPage />} />
                                    <Route path="/collections/:slug" element={<CollectionDetail />} />
                                    <Route path="/product/:slug" element={<ProductDetail />} />
                                    <Route path="/cart" element={<Cart />} />
                                    <Route path="/account" element={<Account />} />
                                    <Route path="/confirmation/:ref" element={<Confirmation />} />
                                    <Route path="/about" element={<CmsPage slug="about" />} />
                                    <Route path="/shipping" element={<CmsPage slug="shipping" />} />
                                    <Route path="/faq" element={<CmsPage slug="faq" />} />
                                    <Route path="/contact" element={<ContactPage />} />
                                    {/* Dynamic custom CMS pages - LAST so it doesn't shadow known routes */}
                                    <Route path="/:slug" element={<DynamicCmsPage />} />
                                </Route>

                                {/* Admin */}
                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                                    <Route index element={<AdminDashboard />} />
                                    <Route path="products" element={<AdminProducts />} />
                                    <Route path="collections" element={<AdminCollections />} />
                                    <Route path="requests" element={<AdminRequests />} />
                                    <Route path="audience" element={<AdminAudience />} />
                                    <Route path="carts" element={<AdminCarts />} />
                                    <Route path="pages" element={<AdminPages />} />
                                    <Route path="navigation" element={<AdminNavigation />} />
                                    <Route path="settings" element={<AdminSettings />} />
                                    <Route path="newsletter" element={<AdminNewsletter />} />
                                    <Route path="messages" element={<AdminMessages />} />
                                </Route>

                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </AdminAuthProvider>
                        </CustomerAuthProvider>
                    </CartProvider>
                    </CurrencyProvider>
                </SiteProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
